/**
 * Contract between packages/ribbon (pure geometry, a port of Nanome's RibbonBuilder.cs + RenderingViewModel.Ribbon.cs)
 * and packages/render (which uploads the buffers). No Three.js, no DOM.
 */
import type { NanomeModel } from '@nanome-lite/data';

/**
 * The part of a NanomeModel the cartoon pipeline reads. A NanomeModel satisfies it; the ribbon worker rebuilds one
 * from the topology it receives once per model plus the current frame's coordinates.
 */
export type RibbonSourceModel = Pick<NanomeModel, 'atomCount' | 'atomName' | 'xyz'> & {
  chains: Pick<NanomeModel['chains'], 'name'>;
  residues: Pick<NanomeModel['residues'], 'count' | 'atomStart' | 'atomCount' | 'chainIndex' | 'name' | 'serial' | 'insCode' | 'ss' | 'substructure'>;
};

/** Cartoon style per residue, after the SS -> style mapping (RenderingViewModel.Ribbon.cs:931-943). */
export const CartoonStyle = { Coil: 0, Helix: 1, Helix3: 2, Helix5: 3, Sheet: 4, NucleicHelix: 5 } as const;
export type CartoonStyleType = (typeof CartoonStyle)[keyof typeof CartoonStyle];

/** One continuous polymer segment (split at chain breaks, non-consecutive residue ids, Cα–Cα >= 5 Å gaps, missing atoms). */
export interface RibbonSegment {
  /** residue indices into the entry's model, in order */
  residueIndex: Uint32Array;
  /** position1: Cα (protein) or C5' (nucleic), Å, interleaved xyz */
  p1: Float32Array;
  /** position2: first O atom (protein) or C3' (nucleic) */
  p2: Float32Array;
  /** position3: base direction atom N1 (G) / N3 (others) for nucleic; zeros for protein */
  p3: Float32Array;
  /** per-residue style */
  style: Uint8Array;
  /** per-residue colour, linear-free sRGB floats, interleaved rgb */
  color: Float32Array;
  nucleic: boolean;
  /** base ring atom positions per residue for nucleic slabs, optional: [residue][ring atoms xyz...] */
  baseRings?: Float32Array[];
}

export interface RibbonMeshBuffers {
  position: Float32Array;   // xyz
  normal: Float32Array;     // xyz
  color: Float32Array;      // rgb per vertex
  /** residue index per vertex, for picking and highlight */
  residueIndex: Uint32Array;
  index: Uint32Array;       // triangle list
  vertexCount: number;
  triangleCount: number;
}

export interface RibbonBuildOptions {
  /** steps per residue: High 8, Medium 4, Low 2, Minimum 1 */
  stepsPerResidue: 8 | 4 | 2 | 1;
  /** reproduce the sheet-smoothing quirk (RibbonBuilder.cs:396,413); default true for screenshot parity */
  reproduceSheetSmoothingQuirk?: boolean;
}

/** Build one mesh for one segment. Deterministic; safe to call in a worker. */
export type BuildRibbon = (segment: RibbonSegment, options: RibbonBuildOptions) => RibbonMeshBuffers;
